import type { MonetaryValue } from 'ezmoney';
import { gql } from 'graphql-tag';

import type {
  CreditNoteValidationAction,
  CreditNote,
} from '@finance-review/models/credit-note';
import { type RawAutomation } from 'modules/bookkeep/apis/prepare-payable/useGetPayableQuery/graphql-reshaper';
import {
  type UkVehicleFuelType,
  type UkVehicleEngineSize,
} from 'modules/requests/mileage/creation/model';

import { type AnalyticalFieldAssociation } from './payable';

export const query = gql`
  query Idbf14431ef412bca3843f8a33e34eddc0($payableId: ID!) {
    __typename
  }
`;

// FIXME: these types should be auto-generated from API schema and sync with the
// related GraphQl query

type RegisteredUserExpenseBeneficiary = {
  id: string;
  organisationId: string;
  companyId: string;
  requestId?: string;
  paymentId?: string;
  type: 'registeredUser';
  registeredUser: {
    id: string;
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    avatarUrl?: string;
    pending: boolean;
  };
};

type UnregisteredUserExpenseBeneficiary = {
  id: string;
  organisationId: string;
  companyId: string;
  requestId?: string;
  paymentId?: string;
  type: 'unregisteredUser';
  unregisteredUser: {
    name: string;
    companyName: string;
  };
};

type ExpenseBeneficiary =
  | RegisteredUserExpenseBeneficiary
  | UnregisteredUserExpenseBeneficiary;

export type RawMember =
  | {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __typename: 'User';
      id: string;
      givenName: string;
      familyName: string;
      email: string;
      avatar: string;
    }
  | {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __typename: 'SuspendedUser';
      id: string;
      givenName: string;
      familyName: string;
    }
  | {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __typename: 'Invitee';
      id: string;
      email: string;
    };

export type RawSupplier = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __typename: 'Supplier';
  id: string;
  name: string;
  thumbnailUrl?: string;
};

export type RawTeam = {
  id: string;
  name: string;
};

export type RawCounterparty = RawMember | RawSupplier;

type RawCostCenter = {
  id: string;
  name: string;
};

export type RawAccountPayable = {
  id: string;
  generalAccountCode: string;
  auxiliaryAccountCode: string | undefined;
};

export type RawDocumentaryEvidenceCannotBeProvided = {
  reportMethod: 'manual' | 'automatic';
  reason: string;
  reporter:
    | {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        __typename: 'User';
        givenName: string;
      }
    | {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        __typename: 'SuspendedUser';
        givenName: string;
      }
    | undefined;
};

export type RawDocumentaryEvidence = {
  type:
    | 'invoice'
    | 'declarationOfMissingInvoice'
    | 'perDiemEntry'
    | 'mileageEntry'
    | 'creditNote';
  invoiceNumber?: string;
  creditNoteNumber?: string;
  validity: {
    valid: boolean;
    reason?: string;
  };
  attachments: {
    mediaType: string;
    url: string;
    path: string;
    downloadUrl: string;
    downloadXmlUrl?: string;
    thumbnailUrl: string;
    attachedAt?: string;
    compliances: {
      spainEreceipt: {
        receipt: {
          path: string;
          uniqueId: string;
        };
      } | null;
    };
  }[];
  compliances: {
    spainEreceipt?: {
      receipt: {
        path: string;
        uniqueId: string;
      };
    };
  };
};

export type RawItemLine = {
  grossAmount: MonetaryValue;
  netAmount: MonetaryValue;
  taxAmount: MonetaryValue;
  taxAccount?: {
    id: string;
    name: string;
    isArchived: boolean;
    rate?: number | string;
  };
  taxAccountId?: string | null;
  expenseAccount?: {
    id: string;
    name: string;
    isArchived: boolean;
  };
  expenseAccountId?: string | null;
  analyticalFieldAssociations?: AnalyticalFieldAssociation[];
};

export type RawCustomField =
  | {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __typename: 'CustomFieldListAssociation';
      customField: {
        id: string;
        name: string;
      };
      values: {
        id: string;
        value: string;
      }[];
    }
  | {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      __typename: 'CustomFieldBooleanAssociation';
      customField: {
        id: string;
        name: string;
      };
      value: boolean;
    };

export type RawAllocation = {
  amount: MonetaryValue;
  reversal?: {
    id: string;
    documentaryEvidence?: {
      creditNoteNumber: string;
    };
  };
  expense?: {
    id: string;
    documentaryEvidence?: {
      invoiceNumber?: string;
    };
  };
  settlement?: {
    kind: 'payment' | 'refund' | 'reimbursement';
    transaction: {
      medium: 'card' | 'wiretransfer';
      description: string;
      clearingDate: string;
    };
  };
};

export type RawPayableType =
  | 'card_purchase'
  | 'invoice_purchase'
  | 'expense_claim'
  | 'mileage_allowance'
  | 'per_diem_allowance'
  | 'reversal';

type RawPayableSubType =
  | 'plasticCard'
  | 'subscriptionCard'
  | 'singleUseCard'
  | 'creditNote';

export type RawPayableState =
  | 'created'
  | 'prepared'
  | 'notBookkept'
  | 'unprepared'
  | 'discarded'
  | 'to_accounting_pending'
  | 'to_accounting_failed'
  | 'in_accounting'
  | 'in_accounting_manually';

export type RawMileageDetails = {
  scheme: {
    type: 'french' | 'german' | 'uk' | 'custom';
    rate: string | null;
  };
  journey: { lat: number; lng: number }[];
  departure: { address: string };
  arrival: { address: string };
  locations: { address: string }[];
  travelDate: string;
  distance: { value: number; unit: string };
  distanceFromMap: { value: number; unit: string };
  passengers: number;
  vehicle: {
    type: 'car' | 'motorcycle' | 'bike';
    taxHorsepower: number;
    totalDistanceOverYear: { value: number; unit: string };
    electric: boolean;
    ownership: 'company' | 'personal';
    fuelType: UkVehicleFuelType;
    engineSize: UkVehicleEngineSize;
  };
};

export type RawPerDiem = {
  tripDays: {
    stays: { arrivalTime: string | null; departureTime: string | null }[];
  }[];
};

export type RawCreditNoteDetails = {
  creditNoteNumber: string;
  referenceInvoiceRequestNumber: string | null;
  referenceInvoiceRequestId: string | null;
  issueDate: Date;
  validationAction: CreditNoteValidationAction | undefined;
  state: CreditNote['state'];
};

export type RawSpendingAllocationPeriod = {
  period: {
    from: string;
    to: string;
  };
  date?: string;
};

export type RawResult = {
  payable: {
    id: string;
    version: number;
    kind: 'expense' | 'reversal';
    type: RawPayableType;
    subtype?: RawPayableSubType;
    state: RawPayableState;
    description: string;
    grossAmount: MonetaryValue;
    functionalExchangeRate: { amount: number; precision: number };
    functionalAmount: MonetaryValue;
    firstExportedAt: string | null;
    counterparty?: RawCounterparty;
    member?: RawMember;
    team?: RawTeam;
    supplier?: RawSupplier;
    costCenter?: RawCostCenter;
    accountPayable?: RawAccountPayable;
    documentaryEvidence?: RawDocumentaryEvidence;
    documentaryEvidenceCannotBeProvided?: RawDocumentaryEvidenceCannotBeProvided;
    itemLines: RawItemLine[];
    customFields: RawCustomField[];
    allocations: RawAllocation[];
    creationDate: string;
    mileageDetails?: RawMileageDetails;
    perDiem?: RawPerDiem;
    creditNoteDetails?: RawCreditNoteDetails;
    spendingAllocationPeriod?: RawSpendingAllocationPeriod;
    accountingDate?: string;
    automation?: RawAutomation;
    amortisation?: {
      schemeId?: string;
      schemeName?: string;
      date: {
        from: string;
        to: string;
      };
    };
    expenseBeneficiaries?: ExpenseBeneficiary[];
  } | null;
};
